import dayjs from 'dayjs';

/**
 * Convert array of objects to CSV string
 * @param {Array} data - Array of objects
 * @param {Array} headers - Array of header strings
 * @returns {string} CSV string
 */
export const arrayToCSV = (data, headers) => {
  if (!data || data.length === 0) return '';

  const csvRows = [];

  // Add headers
  csvRows.push(headers.join(','));

  // Add data rows
  for (const row of data) {
    const values = headers.map(header => {
      const key = header.toLowerCase().replace(/ /g, '_');
      let value = row[key] || '';

      // Escape quotes and wrap in quotes if contains comma
      if (typeof value === 'string') {
        value = value.replace(/"/g, '""');
        if (value.includes(',') || value.includes('\n')) {
          value = `"${value}"`;
        }
      }

      return value;
    });
    csvRows.push(values.join(','));
  }

  return csvRows.join('\n');
};

/**
 * Parse CSV string to array of objects
 * @param {string} csvString - CSV string
 * @returns {Array} Array of objects
 */
export const csvToArray = (csvString) => {
  const lines = csvString.split('\n').filter(line => line.trim());
  if (lines.length === 0) return [];

  const headers = lines[0].split(',').map(h => h.trim());
  const data = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    const obj = {};

    headers.forEach((header, index) => {
      const key = header.toLowerCase().replace(/ /g, '_');
      obj[key] = values[index] || '';
    });

    data.push(obj);
  }

  return data;
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV content
 * @param {string} filename - Filename
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, filename);
  } else {
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

/**
 * Export transactions to CSV
 * @param {Array} transactions - Array of transaction objects
 * @param {Array} goals - Array of goal objects
 * @returns {string} CSV string
 */
export const exportTransactionsToCSV = (transactions, goals) => {
  const data = transactions.map(txn => {
    const goal = goals.find(g => g.id === txn.goalId);
    return {
      date: dayjs(txn.date).format('DD/MM/YYYY'),
      type: txn.type,
      amount: txn.amount,
      goal: goal ? goal.name : 'Unallocated',
      description: txn.description,
    };
  });

  const headers = ['Date', 'Type', 'Amount', 'Goal', 'Description'];
  return arrayToCSV(data, headers);
};

/**
 * Export goals to CSV
 * @param {Array} goals - Array of goal objects
 * @param {number} portfolioValue - Current portfolio value
 * @returns {string} CSV string
 */
export const exportGoalsToCSV = (goals, portfolioValue) => {
  const data = goals.map(goal => ({
    name: goal.name,
    target_amount: goal.targetAmount,
    target_date: dayjs(goal.targetDate).format('DD/MM/YYYY'),
    allocation_percentage: goal.allocationPercentage,
    current_value: portfolioValue * (goal.allocationPercentage / 100),
    monthly_contribution: goal.monthlyContribution,
  }));

  const headers = ['Name', 'Target Amount', 'Target Date', 'Allocation Percentage', 'Current Value', 'Monthly Contribution'];
  return arrayToCSV(data, headers);
};

/**
 * Export portfolio snapshots to CSV
 * @param {Array} snapshots - Array of snapshot objects
 * @returns {string} CSV string
 */
export const exportSnapshotsToCSV = (snapshots) => {
  const data = snapshots.map(snap => ({
    date: dayjs(snap.date).format('DD/MM/YYYY'),
    value: snap.value,
    cost_basis: snap.costBasis,
    gain_loss: snap.value - snap.costBasis,
  }));

  const headers = ['Date', 'Value', 'Cost Basis', 'Gain/Loss'];
  return arrayToCSV(data, headers);
};

/**
 * Export holdings to CSV
 * @param {Array} holdings - Array of holding objects
 * @returns {string} CSV string
 */
export const exportHoldingsToCSV = (holdings) => {
  const data = holdings.map(holding => ({
    name: holding.name,
    symbol: holding.symbol,
    quantity: holding.quantity,
    average_price: holding.averagePrice,
    current_price: holding.currentPrice,
    value: holding.value,
    percentage: holding.percentage,
  }));

  const headers = ['Name', 'Symbol', 'Quantity', 'Average Price', 'Current Price', 'Value', 'Percentage'];
  return arrayToCSV(data, headers);
};

/**
 * Import transactions from CSV
 * @param {string} csvContent - CSV content
 * @param {string} portfolioId - Portfolio ID
 * @param {Array} goals - Array of goal objects
 * @returns {Array} Array of transaction objects
 */
export const importTransactionsFromCSV = (csvContent, portfolioId, goals) => {
  const data = csvToArray(csvContent);

  return data.map((row, index) => {
    const goal = goals.find(g => g.name.toLowerCase() === row.goal?.toLowerCase());

    return {
      id: `txn-import-${Date.now()}-${index}`,
      portfolioId,
      goalId: goal?.id || null,
      type: row.type?.toLowerCase() || 'contribution',
      amount: parseFloat(row.amount) || 0,
      date: dayjs(row.date, 'DD/MM/YYYY').isValid()
        ? dayjs(row.date, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD'),
      description: row.description || '',
      createdAt: dayjs().format('YYYY-MM-DD'),
    };
  });
};

/**
 * Get sample CSV format for transactions
 * @returns {string} Sample CSV string
 */
export const getSampleTransactionsCSV = () => {
  return `Date,Type,Amount,Goal,Description
01/01/2024,contribution,1000,Retirement,Monthly contribution
15/01/2024,dividend,50,Unallocated,Dividend payment
01/02/2024,contribution,1000,Retirement,Monthly contribution`;
};

/**
 * Get sample CSV format for portfolio updates
 * @returns {string} Sample CSV string
 */
export const getSamplePortfolioUpdateCSV = () => {
  return `Date,Value,Cost Basis
01/01/2024,100000,95000
01/02/2024,102000,96000
01/03/2024,104000,97000`;
};

/**
 * Import transactions with portfolio and pot allocations from CSV
 * @param {string} csvContent - CSV content
 * @param {Array} portfolios - Array of portfolio objects
 * @param {Array} pots - Array of pot objects
 * @returns {Array} Array of transaction objects
 */
export const importTransactionsWithAllocationsFromCSV = (csvContent, portfolios, pots) => {
  const data = csvToArray(csvContent);

  return data.map((row, index) => {
    // Find portfolio by name
    const portfolio = portfolios.find(p => p.name.toLowerCase() === row.portfolio?.toLowerCase());
    const portfolioId = portfolio?.id || (portfolios.length > 0 ? portfolios[0].id : null);

    // Parse pot allocations from the "Pots" column
    // Format: "Pot Name 1:50%,Pot Name 2:30%,Pot Name 3:20%"
    const potAllocations = [];
    if (row.pots && row.pots.trim() !== '') {
      const potEntries = row.pots.split(',').map(entry => entry.trim());

      for (const entry of potEntries) {
        const [potName, percentageStr] = entry.split(':').map(s => s.trim());
        const percentage = parseFloat(percentageStr?.replace('%', '')) || 0;

        // Find pot by name within the portfolio
        const pot = pots.find(p =>
          p.portfolioId === portfolioId &&
          p.name.toLowerCase() === potName?.toLowerCase()
        );

        if (pot && percentage > 0) {
          potAllocations.push({
            potId: pot.id,
            percentage: percentage
          });
        }
      }
    }

    return {
      id: `txn-import-${Date.now()}-${index}`,
      portfolioId,
      type: row.type?.toLowerCase() || 'contribution',
      amount: parseFloat(row.amount) || 0,
      date: dayjs(row.date, 'DD/MM/YYYY').isValid()
        ? dayjs(row.date, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD'),
      from: row.from || '',
      potAllocations,
      description: row.description || '',
      createdAt: dayjs().format('YYYY-MM-DD'),
    };
  });
};

/**
 * Export transactions with portfolio and pot allocations to CSV
 * @param {Array} transactions - Array of transaction objects
 * @param {Array} portfolios - Array of portfolio objects
 * @param {Array} pots - Array of pot objects
 * @returns {string} CSV string
 */
export const exportTransactionsWithAllocationsToCSV = (transactions, portfolios, pots) => {
  const data = transactions.map(txn => {
    const portfolio = portfolios.find(p => p.id === txn.portfolioId);

    // Format pot allocations as "Pot Name 1:50%,Pot Name 2:30%"
    let potsString = '';
    if (txn.potAllocations && txn.potAllocations.length > 0) {
      potsString = txn.potAllocations.map(alloc => {
        const pot = pots.find(p => p.id === alloc.potId);
        return pot ? `${pot.name}:${alloc.percentage}%` : '';
      }).filter(s => s).join(',');
    }

    return {
      date: dayjs(txn.date).format('DD/MM/YYYY'),
      type: txn.type,
      amount: txn.amount,
      portfolio: portfolio ? portfolio.name : '',
      pots: potsString,
      from: txn.from || '',
      description: txn.description || '',
    };
  });

  const headers = ['Date', 'Type', 'Amount', 'Portfolio', 'Pots', 'From', 'Description'];
  return arrayToCSV(data, headers);
};

/**
 * Import transactions from Inflow/Outflow CSV format (e.g., from bank/budget apps)
 * @param {string} csvContent - CSV content
 * @param {Array} portfolios - Array of portfolio objects
 * @param {Array} pots - Array of pot objects
 * @param {Function} addPot - Function to add a new pot (optional)
 * @returns {Promise<Array>} Array of transaction objects
 */
export const importTransactionsInflowOutflowCSV = async (csvContent, portfolios, pots, addPot = null) => {
  const data = csvToArray(csvContent);
  const transactions = [];
  const newPots = new Map(); // Track new pots to create

  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    
    try {
      // Skip empty rows
      if (!row.date || (!row.inflow && !row.outflow)) continue;

      // Find portfolio by Account name (or use first portfolio as default)
      const portfolio = portfolios.find(p => p.name.toLowerCase() === row.account?.toLowerCase());
      const portfolioId = portfolio?.id || (portfolios.length > 0 ? portfolios[0].id : null);
      
      if (!portfolioId) {
        console.warn(`Skipping row ${i + 1}: No portfolio found`);
        continue;
      }

      // Parse amounts (handle currency symbols and commas)
      const parseAmount = (val) => {
        if (!val) return 0;
        const num = parseFloat(val.replace(/[^0-9.-]+/g, ''));
        return isNaN(num) ? 0 : num;
      };

      let outflow = parseAmount(row.outflow);
      let inflow = parseAmount(row.inflow);
      
      if (outflow === 0 && inflow === 0) {
        console.warn(`Skipping row ${i + 1}: No valid amount found`);
        continue;
      }

      // Determine transaction type and amount
      let type, amount;
      if (inflow > 0) {
        type = 'contribution';
        amount = inflow;
      } else {
        type = 'withdrawal';
        amount = outflow;
      }

      // Use Pot or Category column for pot name
      const potName = row.pot || row.category || 'Uncategorized';

      // Special handling for "Ready to Assign" - these should not be allocated to any pot
      const isReadyToAssign = potName.toLowerCase() === 'ready to assign';

      let pot = null;

      if (!isReadyToAssign) {
        // Find existing pot or create a new one
        pot = pots.find(p =>
          p.portfolioId === portfolioId &&
          p.name.toLowerCase() === potName.toLowerCase()
        );

        // If pot doesn't exist, create a new one
        if (!pot) {
          const potKey = `${portfolioId}-${potName.toLowerCase()}`;

          if (!newPots.has(potKey)) {
            const newPotData = {
              portfolioId,
              name: potName,
              type: 'cash', // Default type
              currentValue: 0,
              targetValue: 0,
              targetDate: null,
              createdAt: dayjs().format('YYYY-MM-DD'),
            };

            // If addPot function is provided, add the pot to the database
            if (typeof addPot === 'function') {
              try {
                // addPot returns a pot with the actual ID it created
                const createdPot = await addPot(newPotData);
                // Store the pot with its actual ID
                newPots.set(potKey, createdPot);
                // Add to local pots array for subsequent lookups with the CORRECT ID
                pots.push(createdPot);
              } catch (error) {
                console.error('Error adding new pot:', error);
                // Continue with the transaction even if pot creation fails
              }
            } else {
              // If no addPot function, generate an ID ourselves
              const newPot = {
                ...newPotData,
                id: `pot-import-${Date.now()}-${i}`,
              };
              newPots.set(potKey, newPot);
              pots.push(newPot);
            }
          }

          pot = newPots.get(potKey);
        }
      }

      // Parse date (try multiple formats)
      let transactionDate;
      const dateFormats = ['DD/MM/YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY'];
      for (const format of dateFormats) {
        if (dayjs(row.date, format, true).isValid()) {
          transactionDate = dayjs(row.date, format).format('YYYY-MM-DD');
          break;
        }
      }
      
      if (!transactionDate) {
        console.warn(`Using current date for row ${i + 1}: Invalid date format '${row.date}'`);
        transactionDate = dayjs().format('YYYY-MM-DD');
      }

      // Create transaction
      const transaction = {
        id: `txn-import-${Date.now()}-${i}`,
        portfolioId,
        type,
        amount,
        date: transactionDate,
        from: row.payee || '',
        potAllocations: pot ? [{
          potId: pot.id,
          percentage: 100
        }] : [], // Empty array for "Ready to Assign" transactions
        description: row.memo || row.description || '',
        createdAt: dayjs().format('YYYY-MM-DD'),
      };

      transactions.push(transaction);

    } catch (error) {
      console.error(`Error processing row ${i + 1}:`, error, row);
    }
  }

  return transactions;
};

/**
 * Export transactions to Inflow/Outflow CSV format
 * @param {Array} transactions - Array of transaction objects
 * @param {Array} portfolios - Array of portfolio objects
 * @param {Array} pots - Array of pot objects
 * @returns {string} CSV string
 */
export const exportTransactionsInflowOutflowCSV = (transactions, portfolios, pots) => {
  const rows = [];

  transactions.forEach(txn => {
    const portfolio = portfolios.find(p => p.id === txn.portfolioId);

    // If transaction has pot allocations, create a row for each pot
    if (txn.potAllocations && txn.potAllocations.length > 0) {
      txn.potAllocations.forEach(alloc => {
        const pot = pots.find(p => p.id === alloc.potId);
        const allocatedAmount = txn.amount * (alloc.percentage / 100);

        rows.push({
          account: portfolio ? portfolio.name : '',
          date: dayjs(txn.date).format('DD/MM/YYYY'),
          payee: txn.from || '',
          category: pot ? pot.name : '',
          memo: txn.description || '',
          outflow: txn.type === 'withdrawal' ? allocatedAmount.toFixed(2) : '0.00',
          inflow: txn.type === 'contribution' ? allocatedAmount.toFixed(2) : '0.00',
        });
      });
    } else {
      // No pot allocations - create a single row
      rows.push({
        account: portfolio ? portfolio.name : '',
        date: dayjs(txn.date).format('DD/MM/YYYY'),
        payee: txn.from || '',
        category: '',
        memo: txn.description || '',
        outflow: txn.type === 'withdrawal' ? txn.amount.toFixed(2) : '0.00',
        inflow: txn.type === 'contribution' ? txn.amount.toFixed(2) : '0.00',
      });
    }
  });

  const headers = ['Account', 'Date', 'Payee', 'Category', 'Memo', 'Outflow', 'Inflow'];
  return arrayToCSV(rows, headers);
};

/**
 * Get sample CSV format for Inflow/Outflow transactions
 * @returns {string} Sample CSV string
 */
export const getSampleInflowOutflowCSV = () => {
  return `Account,Date,Payee,Category,Memo,Outflow,Inflow
Main Portfolio,29/11/2025,Our HSBC Account,Girl's School Fees,payback from school fees paid from our hsbc,775.00,0.00
Main Portfolio,15/11/2025,Our HSBC Account,Savings,,0.00,450.00
Main Portfolio,15/11/2025,Gareth,Girl's School Fees,,0.00,775.35
Main Portfolio,02/11/2025,Our HSBC Account,Zanna's Grandma Money,for fixing zannas harp mic,400.00,0.00
Main Portfolio,30/10/2025,Our HSBC Account,Joel Bonus,for cotswolds stay and dinner,696.60,0.00
Main Portfolio,16/10/2025,Our HSBC Account,Zanna's Grandma Money,for garden,3043.36,0.00
Main Portfolio,03/10/2025,Our HSBC Account,Savings,,0.00,450.00`;
};

/**
 * Get sample CSV format for transactions with pot allocations
 * @returns {string} Sample CSV string
 */
export const getSampleTransactionsWithAllocationsCSV = () => {
  return `Date,Type,Amount,Portfolio,Pots,From,Description
01/01/2024,contribution,1000,Main Portfolio,Cash:50%,Stocks:50%,Salary Account,Monthly contribution
15/01/2024,contribution,500,Main Portfolio,Emergency Fund:100%,Savings Account,Emergency savings
01/02/2024,withdrawal,200,Main Portfolio,Cash:100%,Landlord,Rent payment
15/02/2024,contribution,1500,Main Portfolio,Stocks:60%,Bonds:40%,Bonus Payment,Bi-weekly paycheck`;
};

/**
 * Export all data to JSON
 * @param {Object} data - All app data
 * @returns {string} JSON string
 */
export const exportAllDataToJSON = (data) => {
  return JSON.stringify(data, null, 2);
};

/**
 * Download JSON file
 * @param {string} jsonContent - JSON content
 * @param {string} filename - Filename
 */
export const downloadJSON = (jsonContent, filename) => {
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  const link = document.createElement('a');

  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
